import axiosInstance from "../../axiosInstance";
import { API_URLS } from "../../url";
import {
  TodayStatusResponse,
  CheckInResponse,
  CheckOutResponse,
  MonthlySummaryResponse,
  CalendarHoverDay,
  HolidayWorkCheckInPayload,
} from "../../../types/attendance";
import { normalizeApiError } from "../../../utils/normalizeApiError";

const extractData = <T>(res: any): T => {
  if (res?.data?.data !== undefined) return res.data.data;
  if (res?.data?.response !== undefined) return res.data.response;
  if (res?.data !== undefined) return res.data;
  return res;
};

export const getTodayStatus = async (): Promise<TodayStatusResponse> => {
  try {
    const response = await axiosInstance.get(API_URLS.ATTENDANCE_TODAY_STATUS);
    return extractData<TodayStatusResponse>(response);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

// The body is only needed for a Holiday Work check-in; an ordinary day sends
// none.
export const checkIn = async (body?: HolidayWorkCheckInPayload | void): Promise<CheckInResponse> => {
  try {
    const response = await axiosInstance.post(API_URLS.ATTENDANCE_CHECK_IN, body);
    return extractData<CheckInResponse>(response);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

export const checkOut = async (): Promise<CheckOutResponse> => {
  try {
    const response = await axiosInstance.post(API_URLS.ATTENDANCE_CHECK_OUT);
    return extractData<CheckOutResponse>(response);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

export const getMonthlySummary = async (
  startDate: string,
  endDate: string
): Promise<MonthlySummaryResponse> => {
  try {
    const response = await axiosInstance.get(API_URLS.ATTENDANCE_MONTHLY_SUMMARY, {
      params: { startDate, endDate },
    });
    return extractData<MonthlySummaryResponse>(response);
  } catch (error: any) {
    if (error?.status === 400 || error?.message?.toLowerCase().includes('policy')) {
      return { presentDays: 0, lateMarks: 0, leaveDays: 0, attendancePercentage: 0 };
    }
    throw normalizeApiError(error);
  }
};

export const getCalendarHovers = async (
  startDate: string,
  endDate: string
): Promise<CalendarHoverDay[]> => {
  try {
    const response = await axiosInstance.get(API_URLS.ATTENDANCE_CALENDAR_HOVERS, {
      params: { startDate, endDate },
    });
    const data = extractData<any>(response);
    return Array.isArray(data) ? data : [];
  } catch (error: any) {
    if (error?.status === 400 || error?.message?.toLowerCase().includes('policy')) {
      return [];
    }
    throw normalizeApiError(error);
  }
};

export interface MissingCheckOutPayload {
  requestedCheckOutDate: string;
  requestedCheckOutTime: string;
  reason: string;
  notes?: string | null;
}

export interface MissingCheckOutResponse {
  submissionMode: 'REVIEW_REQUIRED' | 'NOTIFICATION_ONLY';
  requesterRoleAtSubmission?: string;
  status: 'PENDING' | 'RECORDED' | string;
  attemptNumber?: number;
  requestsUsed?: number;
  maximumRequests?: number;
  remainingRequests?: number;
  regularizationDeadline?: string;
  regularizationState?: string;
  canSubmit?: boolean;
  canReview?: boolean;
}

export const submitMissingCheckOutRequest = async (
  attendanceRecordUuid: string,
  payload: MissingCheckOutPayload
): Promise<MissingCheckOutResponse> => {
  try {
    const { data } = await axiosInstance.post<MissingCheckOutResponse>(
      `/hrm/attendance/attendance-record/${attendanceRecordUuid}/missing-check-out-request`,
      payload
    );
    return data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

export interface RequestHistoryItem {
  requestUuid: string;
  status: string;
  attemptNumber: number;
}

export interface RegularizationStatusResponse {
  attendanceRecordUuid: string;
  attendanceDate: string;
  attendanceTimezone?: string;
  missingCheckoutStrategy: string;
  state: string;
  latestRequestStatus?: string;
  requestsUsed: number;
  maximumRequests: number;
  remainingRequests: number;
  regularizationDeadline?: string;
  canSubmit: boolean;
  blocksCheckIn: boolean;
  requestHistory?: RequestHistoryItem[];
}

export const getRegularizationStatus = async (
  attendanceRecordUuid: string
): Promise<RegularizationStatusResponse> => {
  try {
    const { data } = await axiosInstance.get<RegularizationStatusResponse>(
      `/hrm/attendance/attendance-record/${attendanceRecordUuid}/regularization-status`
    );
    return data;
  } catch (error) {
    throw normalizeApiError(error);
  }
};

export interface AttendanceRegularizationResponse {
  requestUuid: string;
  attendanceRecordUuid?: string;
  appUserUuid?: string;
  employeeUuid?: string;
  employeeName: string;
  profileImageUrl?: string | null;
  designation?: string | null;
  department?: string | null;
  attendanceDate?: string;
  attendanceTimezone?: string;
  regularizationType?: 'MISSED_CHECK_OUT' | string;
  systemCheckInTime?: string | null;
  requestedCheckOutTime: string;
  requestedCheckOutUtcOffset?: string | null;
  reason?: string;
  notes?: string | null;
  submissionMode?: 'REVIEW_REQUIRED' | 'NOTIFICATION_ONLY';
  requesterRoleAtSubmission?: string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUPERSEDED' | 'RECORDED' | 'CANCELLED' | string;
  closureReason?: 'SUPERSEDED_BY_ATTENDANCE_CORRECTION' | 'REQUESTER_DEACTIVATED' | string | null;
  attemptNumber?: number;
  requestsUsed?: number;
  maximumRequests?: number;
  remainingRequests?: number;
  regularizationDeadline?: string;
  regularizationState?: string;
  canSubmit?: boolean;
  canReview?: boolean;
  canEditAttendance?: boolean;
  effectiveCheckOutTime?: string | null;
  reviewedByUuid?: string | null;
  reviewedByName?: string | null;
  reviewedByProfileImageUrl?: string | null;
  reviewedAt?: string | null;
  reviewerRemarks?: string | null;
  createdAt?: string;
}

export const getRegularizationRequestByUuid = async (
  requestUuid: string
): Promise<AttendanceRegularizationResponse> => {
  try {
    const { data } = await axiosInstance.get<AttendanceRegularizationResponse>(
      `/hrm/attendance/regularization-requests/${requestUuid}`
    );
    return extractData<AttendanceRegularizationResponse>(data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};

export interface RegularizationActionPayload {
  action: 'APPROVED' | 'REJECTED';
  remarks?: string | null;
}

export const actionRegularizationRequest = async (
  requestUuid: string,
  payload: RegularizationActionPayload
): Promise<AttendanceRegularizationResponse> => {
  try {
    const { data } = await axiosInstance.post<AttendanceRegularizationResponse>(
      `/hrm/attendance/regularization-requests/${requestUuid}/action`,
      payload
    );
    return extractData<AttendanceRegularizationResponse>(data);
  } catch (error) {
    throw normalizeApiError(error);
  }
};
